import { Injectable } from '@angular/core';
import { User } from '@mitel/cloudlink-sdk';
import { MiVBProvisioningService } from './mivb-provisioning.service';
import { MiVBPasswordEmailBody, MiVBUserEmailBodyWithExtraDetails, MiVBInfoEmailBody, MiVBInfoEmailPhoneBody, UserTagsDataAfterEmailEvent } from '../interfaces/mivb-emails-interfaces';
import * as _ from 'lodash';
import { TranslateService } from '@ngx-translate/core';
import { MiVBAccountSettingsService } from './mivb-account-settings.service';
import { NotifierService } from 'angular-notifier';
import { AdminService } from './admin.service';
import { AccountsService} from './accounts.service';
import { Account} from '@mitel/cloudlink-sdk';
import { Subject } from 'rxjs';
import { UserMiVBEmailEvents } from '../constants/mivb-general';
import { UserMiVBDeploymentEmailHistoryTagsBody } from '../interfaces/mivb-emails-interfaces';
import { MiCCEmailService } from './micc-email.service';
import { TenantsService } from './tenants.service';
import { AutoGeneratePasswordService } from "@mitel-internal/mivb-components";

export const sipDeviceOptions = [
    '5302_IP',
    '5505_SIP',
    '5603_5613_SIP',
    '5604_5614_SIP',
    '5607_SIP',
    '5610_SIP',
    '5617_SIP',
    '5619_SIP',
    '5624_5634_SIP',
    '612_SipDect',
    '622_SipDect',
    '632_SipDect',
    '650_SipDect',
    '712_SipDect',
    '722_SipDect',
    '732_SipDect',
    '742_SipDect',
    'genericSipPhone',
    'UCEndpoint',
    'CloudLink',
    'zoomPSI'
];

export enum PasswordNames {
    SIP = "sip",
    USERPIN = "userpin",
    APP_PASSWORD = "appPassword"
};
@Injectable({
    providedIn: 'root'
})

/*
This is all expected to be temporary code.
In the future we will use a WorkFlow to accomplish sending mivb deployment emails
*/
export class MiVBEmailService {
    public userTagsChanged = new Subject<UserTagsDataAfterEmailEvent>();

    constructor(
        private accountService: AccountsService,
        private adminService: AdminService,
        private miVBProvisioning: MiVBProvisioningService,
        private translateService: TranslateService,
        private mivbAccountSettingsService: MiVBAccountSettingsService,
        private notifierService: NotifierService,
        private miccEmailService: MiCCEmailService,
        private mivbAutoGeneratePasswordService: AutoGeneratePasswordService,
        private tenantsService: TenantsService
    ) { }

    public getMiVBUserWithLiveSync(user: User): Promise<any> {
        if (!user.accountId || !user.userId || !user.pbxUserId) {
          return Promise.reject('Error getting mivb user: missing user details');
        }
        return this.miVBProvisioning.getMiVBUserLiveSync(user.pbxUserId);
    };

    public getRawMiVBUserServices(user: User): Promise<any> {
        if (!user.accountId || !user.userId || !user.pbxUserId) {
            return Promise.reject('Error getting user services: missing user details');
          }
        return this.miVBProvisioning.getMiVBUserServices(user.pbxUserId);
    };

    private canDeploymentEmailBeSent(cloudLinkUser: any): boolean {
        let canSend = true;
        if (!cloudLinkUser?.userId){
            console.error("Missing userId. Will not send a deployment email to user: ", JSON.stringify(cloudLinkUser));
            canSend = false;
        }
        else if(!cloudLinkUser.accountId){
            console.error("Missing accountId. Will not send a deployment email to user: ", cloudLinkUser.userId);
            canSend = false;
        }
        else if(!cloudLinkUser.email){
            console.error("Missing email. Will not send a deployment email to user: ", cloudLinkUser.userId);
            canSend = false;
        }
        else if(!cloudLinkUser.extension){
            console.error("Missing extension. Will not send a deployment email to user: ", cloudLinkUser.userId);
            canSend = false;
        }
        else if(!cloudLinkUser.pbxUserId){
            console.error("Missing pbxUserId. Will not send a deployment email to user: ", cloudLinkUser.userId);
            canSend = false;
        }

        return canSend;
    }

    /*
    - At a minimum, the cloudLinkUser parameter should be an object with a userId (the cloudLink userId) and the accountId.
    - when getCloudLinkUserAndAccount = true - this means the method should get the cloudLinkUser from admin and should look up the current account to get the account language
    - getCloudLinkUserAndAccount - should never be needed from users-bulk-edit.service since the cloudLinkUser and account details are already known
    - On the other hand, when triggered after saving an mivb configuration, the cloudLink user may have been updated (e.g now it might have an extension), so we have to get a fresh copy of the cloudLink user
    - Normally we don't know the passwords to include in the email (e.g Manage page) - so we have to regenerate them and then update the mivb user.
    - After saving an mivb configuration, we may know the passwords used for the save - so we don't have to regenerate, nor update the mivb user.
    - When triggered after saving an mivb configuration, this operation is happenning in the background, so we want to see notifcation banners for progress updates.
    - On the other hand, when triggered from the Manage page, a modal is being used to show the progress of sending emails in bulk - so we don't need all the notifcation banners
    */
    public async deployMiVBUser(cloudLinkUser: any, languageCode: string, translatedMiVBEmailStrings: any, translatedMiCCEmailStrings: any, miCollabIPFQDN: string | undefined, passwords: any, withNotify: boolean, getCloudLinkUserAndAccount?: boolean): Promise<any> {
        let applicationName = "MiVoice Business";
        let account: Account;
        let defaultLanguageCode  = 'en';
        let userTags: any;
        let okToChangeTags = false;

        if(withNotify){
            this.notifierService.notify('info', `${applicationName} email operation in progress`);
        }
        try{

            if(!cloudLinkUser || !cloudLinkUser.userId || !cloudLinkUser.accountId){
                throw 'missing cloudlink userId or accountId';
            }

            if(getCloudLinkUserAndAccount || !('tags' in cloudLinkUser) || !cloudLinkUser.tags){
                cloudLinkUser = await this.adminService.getUserWithoutFrills(cloudLinkUser.userId, 'tags');
            }

            //If getUserWithoutFrills failed, we don't want to update the tags in the catch block without even knowing the current tags
            //By this point now, we know the current tags, so it's ok to update them if needed
            okToChangeTags = true;

            //Note user might have no tags if they were made in MiCollab or ESM and their user details page
            //has never been loaded in Accounts app
            userTags = cloudLinkUser.tags? _.cloneDeep(cloudLinkUser.tags) : {};

            //email operation started timestamp
            await this.changeUsersMiVBEmailTags(cloudLinkUser.userId, cloudLinkUser.accountId, UserMiVBEmailEvents.INPROGRESS, userTags).then((updatedTags: any) =>{
                userTags = _.cloneDeep(updatedTags);
            });

            if(!this.canDeploymentEmailBeSent(cloudLinkUser)){
                throw 'missing basic cloudLink user details';
            }

            if(getCloudLinkUserAndAccount){
                account = this.accountService.getCurrentAccount();

                if(!account || !account.accountId || account.accountId !== cloudLinkUser.accountId){
                    throw 'missing user\'s account details';
                }
                languageCode = account.languageCode || defaultLanguageCode;
            }

            if(!translatedMiVBEmailStrings){
                languageCode = languageCode || defaultLanguageCode;
                let currentLanguageCode = this.translateService.currentLang;
                translatedMiVBEmailStrings = this.getTranslatedEmailStrings(currentLanguageCode, languageCode, 'mivb_emails');
            }

            //get mivb user with live sync
            let mivbUser = await this.getMiVBUserWithLiveSync(cloudLinkUser);

            if(!miCollabIPFQDN){
                miCollabIPFQDN = await this.mivbAccountSettingsService.getMiCollabIPFQDN(cloudLinkUser.accountId);
            }

            //only need mivbUserServices if micollab is setup for account
            let mivbUserServices = null;
            if(miCollabIPFQDN && miCollabIPFQDN !== 'NONE'){
                mivbUserServices = await this.getRawMiVBUserServices(cloudLinkUser);
            }

            // User could have ZOOM pre-license assigned to without PSI phones added to the configuration
            const zoomCollaboration = Boolean(await this.tenantsService.doesUserHaveZoomLicense(cloudLinkUser.userId));

            let infoEmailResult: MiVBUserEmailBodyWithExtraDetails = await this.getMiVBInfoEmailBody(cloudLinkUser, mivbUser, mivbUserServices, miCollabIPFQDN, languageCode, translatedMiVBEmailStrings, zoomCollaboration);
            let passwordEmailResult: MiVBUserEmailBodyWithExtraDetails | null = this.getMiVBPasswordEmailBody(infoEmailResult.body as MiVBInfoEmailBody, infoEmailResult.passwordsRequired, mivbUserServices, miCollabIPFQDN, translatedMiVBEmailStrings, passwords);

            if(!passwordEmailResult){
                throw `Error creating mivb password email for user: ${cloudLinkUser.userId}`;
            }

            //If we had to generate even one password for the mivb user, then we must update the user on the mivb with the passwords
            if(passwordEmailResult && passwordEmailResult.passwordUpdateRequired === true){
                let results = this.getUpdatePasswordBody(mivbUser, passwordEmailResult); //this will return the body to update the user and remove sipPassword cred from passwordEmail if necessary
                let updateUserBody = results.updateUserBody;
                passwordEmailResult = results.passwordEmailResult;
                await this.miVBProvisioning.updateMiVBUserAsyncWithPolling(cloudLinkUser.accountId, cloudLinkUser.userId, cloudLinkUser.pbxUserId, updateUserBody);
            }

            if(miCollabIPFQDN && miCollabIPFQDN !== 'NONE' && infoEmailResult.body.templateProperties && 'miCollabClient' in infoEmailResult.body.templateProperties){
                infoEmailResult.body = await this.insertMiCollabDeploymentInfo(cloudLinkUser, mivbUserServices, miCollabIPFQDN, infoEmailResult.body as MiVBInfoEmailBody);
            }

            // Check for MiCC info
            try {
                const result = await this.miccEmailService.insertMiCCDetails(cloudLinkUser, translatedMiCCEmailStrings, languageCode, infoEmailResult.body as MiVBInfoEmailBody, passwordEmailResult.body as MiVBPasswordEmailBody, undefined)
                infoEmailResult.body = result.infoEmail;
                passwordEmailResult.body = result.pwEmail;
            } catch { }

            //now send the info email
            await this.adminService.sendEmailByTemplateId( "mivbInfo", cloudLinkUser, infoEmailResult.body);
            //now send the email that includes the new passwords
            await this.adminService.sendEmailByTemplateId( "mivbCredentials",cloudLinkUser, passwordEmailResult.body);

            //email operation completed timestamp
            await this.changeUsersMiVBEmailTags(cloudLinkUser.userId, cloudLinkUser.accountId, UserMiVBEmailEvents.SUCCEEDED, userTags).then((updatedTags: any) =>{
                userTags = _.cloneDeep(updatedTags);
            });

            console.log("Success sending " + applicationName + "  emails for user ", cloudLinkUser.userId );
            if(withNotify){
                this.notifierService.notify('success', `${applicationName} email operation complete`);
            }
            return Promise.resolve();

        } catch (error) {
            if(cloudLinkUser?.userId && cloudLinkUser.userId.length > 0){
                console.error("Error sending " + applicationName + " emails for user ", cloudLinkUser.userId, "-->", error );
                if(okToChangeTags){
                    //email operation failed timestamp
                    await this.changeUsersMiVBEmailTags(cloudLinkUser.userId, cloudLinkUser.accountId, UserMiVBEmailEvents.FAILED, userTags);
                }
            }
            else{
                console.error("Error sending " + applicationName + " emails for user -->" + error );
            }

            if(withNotify){
                this.notifierService.notify('error', `${applicationName} email operation failed`);
            }

            if(error){
                return Promise.reject(error);
            }
            else{
                return Promise.reject(`Error sending ${applicationName} emails`);
            }
        }
    }

    /*
    This method will insert micollab deployment info into the info email body.
    Ideally we would only make the request to GET deployment entries if the user was configured with a phone or service that has a deployment profile > 0.
    Unfortunately, userServices are missing some details. So can't check ahead of time if it makes sense to GET deployment entries.
    So we have to GET deployment entries every time, even if all services have deployment profiles set to no_deploy/0.

    Although a user may have multiple softphones each with a deploymentProfile - hence, multiple deployment entries/qr codes,
    at this time we only will show one deployment entry/QR code in the deployment email.
    We will prioritize the one associated with service 101.
    This will change in the future
    */
    private async insertMiCollabDeploymentInfo(cloudLinkUser: User, mivbUserServices: any, miCollabIPFQDN: string | undefined, infoEmailBody: MiVBInfoEmailBody): Promise<MiVBInfoEmailBody> {
        try {
            //leaving here for now in case userServices improves and becomes a better source of truth about if deployment entries are expected
            //let numExpectedDeploymentEntries = this.getNumberOfExpectedDeploymentEntries(mivbUserServices, miCollabIPFQDN);

            //Get the softPhoneExtension associated with service 101.
            if(mivbUserServices && mivbUserServices.userServices && mivbUserServices.userServices.length > 0 && miCollabIPFQDN && miCollabIPFQDN !== 'NONE'){
                let miCollabWebClient = mivbUserServices.userServices.find(svc => svc.hostedUserServiceClassType && svc.hostedUserServiceClassType === 101);
                let softPhoneExtension = miCollabWebClient && miCollabWebClient.serviceDetails && miCollabWebClient.serviceDetails.softPhoneExtension ? miCollabWebClient.serviceDetails.softPhoneExtension : null;
                //softPhoneExtension may be null.
                //but there could still be deployment records.
                //only send a deployment record without an extension if there are none with an extension
                //right now we can only send 1 QR code - so if there is a softPhoneExtension for service 101, we pick that deployment record,
                //otherwise take the first deployment record with an extension. If there are none - then use the deployment for the entry without an extension.
                //this will all change in future.

                let deploymentEntries;
                deploymentEntries = await this.miVBProvisioning.getMiVBUserDeploymentEntries(cloudLinkUser.pbxUserId);
                let noDeploymentRecordsToSend = true;
                if(deploymentEntries && deploymentEntries.length >0){
                    let miCollabWebDeploymentEntry = softPhoneExtension ? deploymentEntries.find(deployEntry => deployEntry.number && deployEntry.number === softPhoneExtension) : undefined;
                    let userLevelDeploymentEntry = deploymentEntries.find(deployEntry => !('number' in deployEntry) || deployEntry.number === null);
                    let otherDeploymentEntriesExist = deploymentEntries.find(deployEntry => deployEntry.number && deployEntry.number !== softPhoneExtension);
                    let deploymentEntriesToSend: any[] = [];
                    if(miCollabWebDeploymentEntry){
                        //prioritize this one and put it first
                        deploymentEntriesToSend.push(miCollabWebDeploymentEntry);

                    }
                    else if (userLevelDeploymentEntry && !otherDeploymentEntriesExist) {
                        //no softPhoneExtension set for MiCollab Client but MiCollab Client has deployment Profile
                        //if there exists other deployment entries with a number, we'll exclude this miCollabWebDeployment entry
                        //if there are no other deployment entries, we'll send just this miCollabWebDeployment entry
                        deploymentEntriesToSend.push(userLevelDeploymentEntry);

                    }

                    if(otherDeploymentEntriesExist){
                        deploymentEntries.forEach(deployEntry => {
                            if(deployEntry.number && deployEntry.number !== softPhoneExtension){
                                deploymentEntriesToSend.push(deployEntry);
                            }
                        });
                    }

                    //do not use mitel.easydeploy.net links - moving away from that system
                    // -e.g //https://mitel.easydeploy.net/authtoken/${token}
                    for(let i = 0; i < deploymentEntriesToSend.length; ++ i)
                    {
                        let imageSource: string;
                        let token = deploymentEntriesToSend[i].token? deploymentEntriesToSend[i].token : undefined;
                        let serverHost = deploymentEntriesToSend[i].deployServerHost? deploymentEntriesToSend[i].deployServerHost : undefined;
                        let base64Url = btoa(`micollab://edd?Deployu=${serverHost}&Token=${token}`);
                        imageSource = `https://qr.cloudlink.tools/?encoding=base64&content=${base64Url}`;

                        if(token && serverHost){
                            noDeploymentRecordsToSend = false;
                            infoEmailBody.templateProperties['miCollabConfig'] = {
                                imageSource: imageSource,
                                authKey: token,
                                appSetupUrl: `https://${serverHost}/mdpapi/v1/edduriredirect?token=${token}`,
                                windowsClientInstallUrl: `https://${serverHost}/ucs/dl/micollab_pc.msi`
                            };

                            if('userPortal' in infoEmailBody.templateProperties){
                                infoEmailBody.templateProperties['userPortal']['url'] = "https://" + serverHost + "/portal";
                            }

                            if('miCollabClient' in infoEmailBody.templateProperties){
                                infoEmailBody.templateProperties['miCollabClient']['clientDownloadLink'] = "https://" + serverHost + "/ucs/dl/micollab_pc.msi";
                            }

                            if('miCollabWebClient' in infoEmailBody.templateProperties){
                                infoEmailBody.templateProperties['miCollabWebClient']['url'] = "https://" + serverHost + "/ucs/micollab";
                            }

                            if('miCollabAWV' in infoEmailBody.templateProperties){
                                infoEmailBody.templateProperties['miCollabAWV']['url'] = "https://" + serverHost + "/awc";
                            }
                        }
                        else{
                            console.error(`Error reading deployment records for ${cloudLinkUser.userId}`);
                            return Promise.reject(`Error reading deployment records for ${cloudLinkUser.userId}`);
                        }

                        if(deploymentEntriesToSend.length > 1){ //In the future, we'll remove this break so we actually loop through more than just one QR code
                            console.warn("Warning: Multiple QR codes required. Only adding 1 to email for user: ", cloudLinkUser.userId);
                            break;
                        }
                    }
                }

                if(noDeploymentRecordsToSend){
                    //no deployment entries for the user. This could make sense if all phones with a deployment profile are set to no_deploy and
                    //also the MiCollab Client has a deployment profile of no_deploy
                    //In this case, remove certain parts of the info email

                    if("miCollabClient" in infoEmailBody.templateProperties){
                        delete infoEmailBody.templateProperties['miCollabClient'];
                    }

                    if("miCollabWebClient" in infoEmailBody.templateProperties){
                       delete infoEmailBody.templateProperties['miCollabWebClient'];
                    }
                }
            }

            return Promise.resolve(infoEmailBody);

        } catch (err) {
            console.error(`Error creating micollab deployment info for ${cloudLinkUser.userId}: `, err);
            return Promise.reject(`Error creating micollab deployment info for user: ${cloudLinkUser.userId}`);
        }
    }

    //similar logic as setPhoneName method in mivb-components library (phone-item component)
    //except adding prime label, and not translating device type
    private getPhoneTitle(phone: any, phoneType: string, primelabel?: string): string {
        let title = '';
        if(phone["serviceLabel"] && phone["serviceLabel"] !== undefined && phone["serviceLabel"] !== "") {
            title = phone["serviceLabel"];
        } else if (phone["deviceType"] && phone["deviceType"] !== undefined) {
            title= phone["deviceType"];
        } else {
            title = phoneType;
        }

        if (phone["number"] && phone["number"] !== undefined && phone["number"] !== "") {
            title = title + " (" + phone["number"] + ")";
        }

        if(phone.prime === true && primelabel){
            title = primelabel + ': ' + title;
        }
        return title;
    }

    private async findVoicemailAccessNumber(cloudLinkUser: User, phone: any): Promise<any> {
        try {
            let elementId = phone.networkElement && phone.networkElement.length > 0? phone.networkElement : null;
            if(elementId){
                let accessNumber = await this.mivbAccountSettingsService.getMiCollabVoicemailAccessNumber(cloudLinkUser.accountId, elementId);
                console.log("Voicemail access number: ", accessNumber);
                return accessNumber;
            }
            else{
                console.error("Voicemail error. Failed to get network element of prime phone for user: ", cloudLinkUser.userId);
                return Promise.reject(null);
            }
        } catch (error) {
            console.error("Voicemail error. Failed to get access number for user: ", cloudLinkUser.userId);
            return Promise.reject(null);
        }
    }

    //should only be called when phone.prime === true
    //when we incorporate micollab, will have to build this out to check for NuPoint as well
    //In the Nupoint case (advanced), should return similar result to Embedded UM
    private async getVoicemailDetails(cloudLinkUser: User, phone: any, translatedMiVBEmailStrings: any, mivbUserServices: any, miCollabIPFQDN: string | undefined): Promise<any> {
        let voicemailInfo = {
            mailbox: '',
            accessNumber: '',
            emailIntegration: ''
        };

        let vmType: null | 'standard' | 'advanced' = null;
        let email = "";
        let nupoint = false;
        let micollabExists: boolean = miCollabIPFQDN && miCollabIPFQDN !== 'NONE'? true : false;

        if (phone.voicemail &&
            phone.voicemail.active && phone.voicemail.active === true &&
            phone.voicemail.mailboxNumber && phone.voicemail.mailboxNumber !== '' ){
                voicemailInfo.mailbox = phone.voicemail.mailboxNumber;
                if('forwardToEmail' in phone.voicemail && 'enabled' in phone.voicemail.forwardToEmail && phone.voicemail.forwardToEmail.enabled === true){
                    vmType = 'standard';
                }
                else if('embeddedUnifiedMessaging' in phone.voicemail && 'enabled' in phone.voicemail.embeddedUnifiedMessaging && phone.voicemail.embeddedUnifiedMessaging.enabled === true){
                    vmType = 'advanced';
                }
                if(micollabExists){
                    //need to get the access number for EMEM case
                    try {
                        let accessNumber = await this.findVoicemailAccessNumber(cloudLinkUser, phone);
                        voicemailInfo.accessNumber = accessNumber;
                    } catch (error) {
                        return Promise.reject('Failed to get access number for voicemail');
                    }
                }
        }
        else if(micollabExists && mivbUserServices && mivbUserServices.userServices && mivbUserServices.userServices.length > 0){
            let nuPointService = mivbUserServices.userServices.find(svc => svc.hostedUserServiceClassType && svc.hostedUserServiceClassType === 103);
            if (nuPointService &&
                nuPointService.serviceDetails && ('enabled' in nuPointService.serviceDetails)  && nuPointService.serviceDetails.enabled === true &&
                nuPointService.serviceAlias && nuPointService.serviceAlias === phone.number.toString()){
                    console.log("Found nupoint on prime");
                    nupoint = true;
                    voicemailInfo.mailbox = nuPointService.serviceDetails.mailboxNumber;
                    if(('advancedUm' in nuPointService.serviceDetails) && nuPointService.serviceDetails.advancedUm === true){
                        vmType = 'advanced';
                    }
                    else if(('standardUM' in nuPointService.serviceDetails) && nuPointService.serviceDetails.standardUM === true){
                        vmType = 'standard';
                    }

                    //need to get the access number for NuPoint case
                    try {
                        let accessNumber = await this.findVoicemailAccessNumber(cloudLinkUser, phone);
                        voicemailInfo.accessNumber = accessNumber;
                    } catch (error) {
                        return Promise.reject('Failed to get access number for NuPoint');
                    }
                }
        }

        if(vmType === 'standard'){
            if(nupoint){
                email = cloudLinkUser.email? (cloudLinkUser.email + ' ') : '';
            }
            else{
                email = phone.voicemail.forwardToEmail.email? (phone.voicemail.forwardToEmail.email + ' ') : '';
            }
            voicemailInfo.emailIntegration = email + '('+ translatedMiVBEmailStrings['forward_to_email'] + ')';
        }
        else if(vmType === 'advanced'){
            email = cloudLinkUser.email? (cloudLinkUser.email + ' ') : '';
            voicemailInfo.emailIntegration = email + '('+ translatedMiVBEmailStrings['full_synchronization'] + ')';
        }

        return Promise.resolve(voicemailInfo);
    }

    // UserServices are missing some details at the moment. Can't use this now. Maybe in future.
    // private getNumberOfExpectedDeploymentEntries(mivbUserServices: any, miCollabIPFQDN: string | undefined): number {
    //     let count = 0;
    //     if(mivbUserServices && mivbUserServices.userServices && mivbUserServices.userServices.length > 0 && miCollabIPFQDN && miCollabIPFQDN !== 'NONE'){
    //         mivbUserServices.userServices.forEach(service => {
    //             if('serviceDetails' in service && "deploymentProfile" in service.serviceDetails){
    //                 let profileNum = Number(service.serviceDetails.deploymentProfile);
    //                 if(profileNum && !isNaN(profileNum) && profileNum > 0){
    //                     ++count;
    //                 }
    //             }
    //         });
    //     }
    //     return count;
    // }

    private areCompanyCredentialsUsedForMiCollab(mivbUserServices: any, miCollabIPFQDN: string | undefined): boolean {
        if(mivbUserServices && mivbUserServices.userServices && mivbUserServices.userServices.length > 0 && miCollabIPFQDN && miCollabIPFQDN !== 'NONE'){
            let miCollabUCCUserLicense = mivbUserServices.userServices.find(svc => svc.hostedUserServiceClassType && svc.hostedUserServiceClassType === 106);
            if(miCollabUCCUserLicense && 'serviceDetails' in miCollabUCCUserLicense){
                if('clAuthenticated' in miCollabUCCUserLicense.serviceDetails && miCollabUCCUserLicense.serviceDetails.clAuthenticated === true){
                    return true;
                }
                else if ('ids' in miCollabUCCUserLicense.serviceDetails &&
                'authenticated' in miCollabUCCUserLicense.serviceDetails.ids && miCollabUCCUserLicense.serviceDetails.ids.authenticated === true &&
                'manageable' in miCollabUCCUserLicense.serviceDetails.ids && miCollabUCCUserLicense.serviceDetails.ids.manageable === true){
                    return true;
                }
            }
        }

        return false;
    }

    private async getMiVBInfoEmailBody(cloudLinkUser: User, mivbUser: any, mivbUserServices: any, miCollabIPFQDN: string | undefined, languageCode: string, translatedMiVBEmailStrings: any, zoomCollaboration: boolean): Promise<MiVBUserEmailBodyWithExtraDetails> {

        if(!mivbUser.phones || mivbUser.phones.length <=0 ){
            return Promise.reject(`Error creating mivb info email: no phones found for userId: ${cloudLinkUser.userId}`);
        }
        let body: MiVBInfoEmailBody ={
            to: [cloudLinkUser.email],
            languageCode: languageCode,
            templateProperties: {
                userId: cloudLinkUser.userId,
                phones: [],
                zoomCollaboration: zoomCollaboration
            }
        };
        let passwordsRequired = {userpin: null}; //always will need a user pin to apply to all phones
        let sipPasswordRequired = false;

        /*
            In email template but leaving out for now since mapping is unknown:
            - "callerId"
            - "callerName"
            - "emergencyLocation"
            - "phoneServiceHost"
        */

        let wirelessPinDevices = ['5505_SIP', 'netVisionIP'];
        for ( var i = 0; i < mivbUser.phones.length; i++ ){ //Reminder: current 8 phone max in email templates, and user config
            let phone = mivbUser.phones[i];
            let deviceType = "";
            let tempPhone: MiVBInfoEmailPhoneBody = {
                title: '',
                type: '',
                extension: phone.number
            };

            if(phone.deviceType && phone.deviceType !== ''){
                deviceType = phone.deviceType;
                tempPhone["model"] = phone.deviceType;
            }

            if(phone.did && phone.did !== ''){
                tempPhone["directNumbers"] = phone.did; //Reminder: currently only shows one, but user could have more
            }

            if(phone.hotDesk && phone.hotDesk === true){
                tempPhone.type = translatedMiVBEmailStrings['hot_desk'];

                if(phone.preferredSet && phone.preferredSet !== ''){
                    tempPhone["model"] = phone.preferredSet;
                }

                if(phone.externalHotDesk && phone.externalHotDesk === true &&
                    phone.hotdeskExternalNumber && phone.hotdeskExternalNumber !== ''){
                        tempPhone["linkedExternalNumber"] = phone.hotdeskExternalNumber;
                }
            }
            else if(deviceType === 'genericSipPhone'){
                tempPhone.type = translatedMiVBEmailStrings['generic_sip'];
            }
            else {
                tempPhone.type = translatedMiVBEmailStrings['desk_phone'];
            }

            if('model' in tempPhone && tempPhone["model"] && (wirelessPinDevices.includes(tempPhone["model"]))){
                if(!('wirelessPin' in passwordsRequired)){
                    passwordsRequired['wirelessPin'] = null;
                }
            }

            if(phone.prime === true){
                let primelabel = translatedMiVBEmailStrings['prime_phone'];
                tempPhone.title = this.getPhoneTitle(phone, tempPhone.type, primelabel);

                //add the voicemail section only if the prime phone has an active vm box with number
                try {
                    let voicemailInfo = await this.getVoicemailDetails(cloudLinkUser, phone, translatedMiVBEmailStrings, mivbUserServices, miCollabIPFQDN);
                    if(voicemailInfo && voicemailInfo.mailbox !== ''){
                        body.templateProperties["voicemail"] = {
                            mailbox: voicemailInfo.mailbox
                        };
                        if(voicemailInfo.accessNumber !== ''){
                            body.templateProperties.voicemail.accessNumber = voicemailInfo.accessNumber;
                        }
                        if(voicemailInfo.emailIntegration !== ''){
                            body.templateProperties.voicemail.emailIntegration = voicemailInfo.emailIntegration;
                        }
                        passwordsRequired['voicemail'] = null;
                    }
                } catch (error) {
                    return Promise.reject(`Error creating mivb info email: error determining voicemail details for user: ${cloudLinkUser.userId}`);
                }
            }
            else{
                tempPhone.title = this.getPhoneTitle(phone, tempPhone.type);
            }

 
            if(phone.teleWorkerService && phone.teleWorkerService === true &&
                phone.mbgSIPUsername && phone.mbgSIPUsername !== ''){
                    tempPhone["sipUsername"] = phone.mbgSIPUsername;

            }

            if(sipDeviceOptions.includes(deviceType) || phone.sipPassword || phone.mbgSIPPassword){
                sipPasswordRequired = true;
            }

            body.templateProperties.phones.push(tempPhone);
        }

        if(sipPasswordRequired){
            passwordsRequired['sip'] = null;
        }

        if(mivbUserServices && mivbUserServices.userServices && mivbUserServices.userServices.length > 0 && miCollabIPFQDN && miCollabIPFQDN !== 'NONE'){
            let miCollabUCCUserLicense = mivbUserServices.userServices.find(svc => svc.hostedUserServiceClassType && svc.hostedUserServiceClassType === 106);
            let miCollabWebClient = mivbUserServices.userServices.find(svc => svc.hostedUserServiceClassType && svc.hostedUserServiceClassType === 101);
            let miCollabAWV = mivbUserServices.userServices.find(svc => svc.hostedUserServiceClassType && svc.hostedUserServiceClassType === 102);

            if(miCollabUCCUserLicense && miCollabWebClient){ //106 should always be present if 101 is present
                passwordsRequired['miCollabAppPassword'] = null;
                let miCollabUsername = miCollabUCCUserLicense.serviceDetails && miCollabUCCUserLicense.serviceDetails.login ? miCollabUCCUserLicense.serviceDetails.login : '';
                if(miCollabUsername){

                    let isMSVIPresent = false;
                    try {
                        isMSVIPresent = await this.mivbAccountSettingsService.isMSVIPresent();
                    } catch (error) {
                        console.warn("Continuing with MiVB Deployment email operation and assuming MSVI is false");
                    }

                    //don't include /portal link if MSVI
                    if(!isMSVIPresent){
                        body.templateProperties["userPortal"] = { //keep even if no deployment record
                            url: "https://" + miCollabIPFQDN + "/portal", //will be replaced with a different value if a deployment record exists
                            username: miCollabUsername
                        };
                    }
                    body.templateProperties["miCollabClient"] = { //will be removed if no deployment record
                        clientDownloadLink: '', //will be set using serverHost from deployment profile
                        username: miCollabUsername
                    };

                    body.templateProperties["miCollabWebClient"] = { //will be removed if no deployment record
                        url:  '', //will be set using serverHost from deployment profile
                        username: miCollabUsername
                    };

                    if(miCollabAWV){
                        body.templateProperties["miCollabAWV"] = { //keep if 102 is there (even if no deployment record)
                            url: "https://" + miCollabIPFQDN + "/awc", //will be replaced with a different value if a deployment record exists
                            username: miCollabUsername
                        }
                    }
                }
            }
        }

        return Promise.resolve({body: body, passwordsRequired: passwordsRequired});
    };

    private getMiVBPasswordEmailBody(infoBody: MiVBInfoEmailBody, passwordsRequired: any, mivbUserServices: any, miCollabIPFQDN: string | undefined, translatedMiVBEmailStrings: any, passwords: any): MiVBUserEmailBodyWithExtraDetails | null {
        let passwordError = false;
        let passwordUpdateRequired = false;

        let passwordBody: MiVBPasswordEmailBody = {
            to: infoBody.to,
            languageCode: infoBody.languageCode,
            templateProperties: {
                userId: infoBody.templateProperties.userId,
                phoneCredentials: { pin: null }
            }
        };

        if('userpin' in passwordsRequired){
            if(passwords && passwords.userpin){
                passwordsRequired.userpin = passwords.userpin;
            }
            else{
                passwordsRequired.userpin = this.generateUserPin();
                passwordUpdateRequired = true;
            }
            if(passwordsRequired.userpin.length < 4){
                passwordError = true;
            }
            else{
                passwordBody.templateProperties.phoneCredentials.pin = passwordsRequired.userpin;
                if('wirelessPin' in passwordsRequired){
                    passwordBody.templateProperties.phoneCredentials.wirelessPin = (passwordsRequired.userpin).slice(0,4); //always first 4 characters of pin
                    //don't need to update it in passwordsRequired. This key will be removed before the update request since the micro takes care of the wirelessPin update
                }
            }
        }
        else{
            passwordError = true; //should always need a userpin
        }

        if('voicemail' in passwordsRequired && !passwordError){
            passwordsRequired.voicemail = passwordsRequired.userpin;
            passwordBody.templateProperties.voicemail = {passcode: passwordsRequired.voicemail};
        }

        if('sip' in passwordsRequired && !passwordError){
            if(passwords && passwords.sip){
                passwordsRequired.sip = passwords.sip;
            }
            else{
                passwordsRequired.sip = this.generateSipPassword();
                passwordUpdateRequired = true;
            }
            if(!passwordsRequired.sip){
                passwordError = true;
            }
            else{
                passwordBody.templateProperties.phoneCredentials.sipPassword = passwordsRequired.sip;
            }
        }

        if('miCollabAppPassword' in passwordsRequired  && !passwordError){
            if(this.areCompanyCredentialsUsedForMiCollab(mivbUserServices, miCollabIPFQDN) === true){
                let appPswdString = translatedMiVBEmailStrings['use_corporate_credentials'];
                if(infoBody && infoBody.templateProperties && 'userPortal' in infoBody.templateProperties){
                    //userPortal section is to be excluded if MSVI
                    passwordBody.templateProperties.userPortal = {password: appPswdString};
                }
                passwordBody.templateProperties.miCollabApplication = {password: appPswdString};
                delete passwordsRequired['miCollabAppPassword']; //don't want to include appPassword in the PUT request to update passwords since using corporate creds
            }
            else{
                if(passwords && passwords.miCollabAppPassword){
                    passwordsRequired.miCollabAppPassword = passwords.miCollabAppPassword;
                }
                else{
                    passwordsRequired.miCollabAppPassword = this.generateMiCollabAppPassword();
                    passwordUpdateRequired = true;
                }
                if(passwordsRequired.miCollabAppPassword === ''){
                    passwordError = true;
                }
                else{
                    if(infoBody && infoBody.templateProperties && 'userPortal' in infoBody.templateProperties){
                        //userPortal section is to be excluded if MSVI
                        passwordBody.templateProperties.userPortal = {password: passwordsRequired.miCollabAppPassword};
                    }
                    passwordBody.templateProperties.miCollabApplication = {password: passwordsRequired.miCollabAppPassword};
                }
            }
        }

        if(passwordError){
            return null
        }
        else{
            return ({ body: passwordBody, passwordsRequired: passwordsRequired, passwordUpdateRequired: passwordUpdateRequired });
        }
    };

    // This will return the body to update the user with the new password
    // It will also remove the sipPassword cred from passwordEmail if necessary (example: only sip is MCXSofphone)
    private getUpdatePasswordBody(mivbUser: any, passwordEmailResult: MiVBUserEmailBodyWithExtraDetails): any {
        //backend handles wireless pin automatically from tuiPassCode
        //we also need to strip off the baseTemplateId so this does not turn into a Upgrade/Downgrade of the user
        let keysToRemove = ['accountId', 'pbxUserId', 'callHandling', 'wirelessPin', 'baseTemplateId'];
        let passwordsRequired = passwordEmailResult.passwordsRequired;
        let updateUserBody = _.cloneDeep(mivbUser);
        let showSipPasswordInCredsEmails = false; //omit when the only sip phone is MCXSoftphone (isMitelCxWebRtcPhone)

        keysToRemove.forEach(key => delete updateUserBody[key]);

        if('userpin' in passwordsRequired){
            updateUserBody['tuiPassCode'] = passwordsRequired.userpin;
        }

        if('sip' in passwordsRequired)
        {
            for ( var i = 0; i < updateUserBody.phones.length; i++ ){
                if('sipPassword' in updateUserBody.phones[i]){
                    if( updateUserBody.phones[i]?.isMitelCxWebRtcPhone === true){
                        updateUserBody.phones[i].sipPassword = passwordsRequired.userpin; //for MCX Softphones, the sipPassword must match the tui
                    }
                    else{
                        updateUserBody.phones[i].sipPassword = passwordsRequired.sip;
                        showSipPasswordInCredsEmails = true;
                    }
                }
                if('mbgSIPPassword' in updateUserBody.phones[i]){
                    updateUserBody.phones[i].mbgSIPPassword = passwordsRequired.sip;
                    if( updateUserBody.phones[i]?.isMitelCxWebRtcPhone !== true){
                        showSipPasswordInCredsEmails = true;
                    }
                }
            }

            if(!showSipPasswordInCredsEmails){
                let passwordEmailBody = passwordEmailResult.body as MiVBPasswordEmailBody;
                if(passwordEmailBody.templateProperties?.phoneCredentials?.sipPassword){
                    delete passwordEmailBody.templateProperties.phoneCredentials.sipPassword
                    passwordEmailResult.body = passwordEmailBody;
                }
            }
        }

        if ('miCollabAppPassword' in passwordsRequired) { //miCollabAppPassword will be removed from passwordsRequired in getMiVBPasswordEmailBody() if corporate creds are in use
            updateUserBody['appPassword'] = passwordsRequired.miCollabAppPassword;
        }
        else if ('appPassword' in updateUserBody){
            delete updateUserBody['appPassword']; //don't include appPassword in the user update body if it wasn't changed
        }

        return {updateUserBody: updateUserBody, passwordEmailResult: passwordEmailResult};
    };

    private generateUserPin(){
        let newUserPin = this.createPassword(PasswordNames.USERPIN);
        return newUserPin;
    };

    private generateSipPassword(){
       let newSipPassword = this.createPassword(PasswordNames.SIP);
       return newSipPassword
    };

    private generateMiCollabAppPassword() {
        let newAppPassword = this.createPassword(PasswordNames.APP_PASSWORD);
        return newAppPassword
    }

    private createPassword(passwordName: PasswordNames){
        return this.mivbAutoGeneratePasswordService.createPassword(passwordName);
    }

    //need to pre-translate strings in the language of the account (not the app setting)
    public getTranslatedEmailStrings(currentlLanguageCode: string, desiredLanguageCode: string, name: string): any {
        this.translateService.use(desiredLanguageCode);
        let translatedEmailStrings = this.translateService.getParsedResult(this.translateService.translations[desiredLanguageCode], name);
        this.translateService.use(currentlLanguageCode);
        return translatedEmailStrings;
    };

    private async changeUsersMiVBEmailTags(userId: string, accountId: string, tagName: UserMiVBEmailEvents, existingTags: any): Promise<any>{
        let newTags: UserMiVBDeploymentEmailHistoryTagsBody;
        let currentTime = new Date().toISOString();
        let actionType: string = 'create';

        try {
            if(existingTags?.mivb &&
                "deploymentEmailHistory" in existingTags.mivb &&
                "inProgressOn" in existingTags.mivb.deploymentEmailHistory &&
                "succeededOn" in existingTags.mivb.deploymentEmailHistory &&
                "failedOn" in existingTags.mivb.deploymentEmailHistory &&
                "latest" in existingTags.mivb.deploymentEmailHistory)
            {
                //user has exisitng deploymentEmailHistory tags in the correct structure
                newTags = _.cloneDeep(existingTags.mivb.deploymentEmailHistory);
                actionType = 'update'; //just need to update tags

            }
            else {
                if(existingTags?.mivb){
                    actionType = 'update'; //mivb tag already exists but does not contain correct structure of deploymentEmailHistory
                }
                //user does not have deploymentEmailHistory tags or they are in a bad state
                //re-initialize them
                newTags = {
                    inProgressOn: '',
                    succeededOn: '',
                    failedOn: '',
                    latest: ''
                };
            }

            switch (tagName) {
                case UserMiVBEmailEvents.INPROGRESS:
                    newTags.inProgressOn = currentTime;
                    newTags.latest = UserMiVBEmailEvents.INPROGRESS
                    break;
                case UserMiVBEmailEvents.SUCCEEDED:
                    newTags.succeededOn = currentTime;
                    newTags.latest = UserMiVBEmailEvents.SUCCEEDED
                    break;
                default:
                    newTags.failedOn = currentTime;
                    newTags.latest = UserMiVBEmailEvents.FAILED
                    break;
            }

            let createOrUpdateTags: any;
            if(actionType === 'create'){
                createOrUpdateTags = {
                    mivb: {
                        deploymentEmailHistory: newTags
                    }
                };
            }
            else { //update
                let mivbTags = _.cloneDeep(existingTags.mivb);
                mivbTags['deploymentEmailHistory'] = newTags;
                createOrUpdateTags = {
                    mivb: mivbTags
                };
            }

            let response = await this.adminService.createUserTagByAccountIdWithoutSpinner(accountId, userId, createOrUpdateTags);

            //manage page will need to know tags were updated otherwise, if they press send deployment emails again, we get old user tags
            //userEdit page will need to know when timestamps have been updated for showing the mivb email icon
            this.userTagsChanged.next({userId: userId,  accountId: accountId, tags: response});
            return Promise.resolve(response);

        } catch (error) {
            //if creating/updating deploymentEmailHistory tags failed
            console.error(`Failed to update or create deployment history tags for user: ${userId}`);
            return Promise.reject(error);
        }
    }
};